import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { encryptedCookieStorage } from '@/lib/encrypted-cookie-storage';
import { tokenStorage } from '@/lib/storage';
import type { AuthUser } from '@/features/auth/types';

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  /**
   * Epoch ms after which this BFM Online session is no longer valid —
   * mirrors the validated Barita access token's `exp` claim (see
   * `jwt-validate.ts`), capped by `expires_in` when the auth server
   * provides it. Once passed, `isSessionExpired()` reports true and the
   * route guard sends the user back to the Barita auth server.
   */
  expiresAt: number | null;
  /**
   * When this session started signing in, the previous session's login was
   * captured here — that's what the account menu shows as "Last login" (the
   * current login's own timestamp isn't useful to display as "last login").
   * `null` means this is the first login ever recorded on this browser.
   */
  previousLoginAt: string | null;
  /** This session's own login timestamp — becomes `previousLoginAt` next time. */
  lastLoginAt: string | null;
  /**
   * Establish a BFM Online session from a Barita access token. Callers must
   * only invoke this after `validateAccessToken` has confirmed the token's
   * signature, issuer, audience, and expiration — never from raw, unverified
   * redirect params.
   */
  establishSession: (user: AuthUser, accessToken: string, expiresAt: number) => void;
  /** Whether the current session's expiry has passed. `false` once logged out (no active session to expire). */
  isSessionExpired: () => boolean;
  /** Clear the session + token. Does not itself redirect — callers decide when to send the user back to Barita. */
  logout: () => void;
}

/**
 * Client-only auth UI state (who is signed in). The bearer token itself
 * lives in `tokenStorage`; this store mirrors only the user profile + session
 * expiry, and persists just enough to survive a page reload.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      expiresAt: null,
      previousLoginAt: null,
      lastLoginAt: null,
      establishSession: (user, accessToken, expiresAt) => {
        tokenStorage.setToken(accessToken);
        set((state) => ({
          user,
          isAuthenticated: true,
          expiresAt,
          previousLoginAt: state.lastLoginAt,
          lastLoginAt: new Date().toISOString(),
        }));
      },
      isSessionExpired: () => {
        const { expiresAt } = get();
        return expiresAt !== null && Date.now() >= expiresAt;
      },
      logout: () => {
        tokenStorage.clear();
        set({ user: null, isAuthenticated: false, expiresAt: null });
      },
    }),
    {
      name: 'bfmonline.auth',
      // Encrypted session cookie, not plaintext localStorage/sessionStorage:
      // this persists the user's profile (name, email, roles), which must
      // not show up as readable JSON in DevTools storage. See
      // `encrypted-cookie-storage.ts` for what this does and doesn't
      // protect against. The cookie is session-only (no Max-Age), so it
      // doesn't outlive the browser any more than the bearer token in
      // `tokenStorage` does.
      storage: createJSONStorage(() => encryptedCookieStorage),
      // Only persist the profile + flags; the token itself lives in tokenStorage.
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        expiresAt: state.expiresAt,
        previousLoginAt: state.previousLoginAt,
        lastLoginAt: state.lastLoginAt,
      }),
    },
  ),
);
